import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { SwitchField, TextField } from '@/components/form-fields';
import {
  AddButton,
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
  SettingsSkeleton,
} from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import {
  createVehicle,
  DEFAULT_VALUE_CAP_CENTS,
  deleteVehicle,
  listVehicles,
  updateVehicle,
  type VehicleRecord,
  VIN_PATTERN,
  vehicleInUse,
} from '@/lib/settings/fleet';
import { useForm } from '@/lib/use-form';

/**
 * The delivery fleet.
 *
 * Each vehicle is paired with a `type = 'vehicle'` location, because stock in
 * transit is held against a location like any other stock -- the pair is
 * created, renamed and retired together so the dispatch board never offers a
 * van whose location was quietly deactivated.
 */

const schema = z.object({
  name: z.string().trim().min(1, 'Name the vehicle').max(120),
  make: z.string().trim().max(60),
  model: z.string().trim().max(60),
  year: z
    .string()
    .trim()
    .regex(/^(19|20)\d{2}$/, 'A four-digit year')
    .or(z.literal('')),
  color: z.string().trim().max(40),
  licensePlate: z.string().trim().max(20),
  vin: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || VIN_PATTERN.test(value),
      'A VIN is 11-17 characters, no I, O or Q',
    ),
  insuranceExpiresAt: z.string().trim(),
  registrationExpiresAt: z.string().trim(),
  valueCap: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, 'Amounts look like 10000.00'),
  active: z.boolean(),
});

type Values = z.infer<typeof schema>;

function toValues(vehicle: VehicleRecord | null): Values {
  return {
    name: vehicle?.name ?? '',
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    year: vehicle?.year ? String(vehicle.year) : '',
    color: vehicle?.color ?? '',
    licensePlate: vehicle?.licensePlate ?? '',
    vin: vehicle?.vin ?? '',
    insuranceExpiresAt: vehicle?.insuranceExpiresAt ?? '',
    registrationExpiresAt: vehicle?.registrationExpiresAt ?? '',
    valueCap: ((vehicle?.capacityValueCapCents ?? DEFAULT_VALUE_CAP_CENTS) / 100).toFixed(2),
    active: vehicle?.active ?? true,
  };
}

export function VehicleSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('fleet.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<VehicleRecord | 'new' | null>(null);

  const vehicles = useQuery({
    queryKey: ['vehicles', accountId],
    queryFn: () => listVehicles(accountId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['vehicles', accountId] });
    queryClient.invalidateQueries({ queryKey: ['locations', accountId] });
  };

  const remove = useMutation({
    mutationFn: async (vehicle: VehicleRecord) => {
      const open = await vehicleInUse(accountId, vehicle.id);

      if (open > 0) {
        throw new Error('A driver is out in this vehicle right now. End the session first.');
      }

      await deleteVehicle(accountId, vehicle);
    },
    onSuccess: () => {
      toast.success('Vehicle removed.');
      setEditing(null);
      refresh();
    },
    onError: (error: Error) =>
      toast.error(
        error.message.includes('violates foreign key')
          ? 'This vehicle has delivery history. Deactivate it instead.'
          : error.message,
      ),
  });

  if (vehicles.isLoading) return <SettingsSkeleton />;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="fleet.manage" />}

      <SectionCard
        title="Vehicles"
        description="The vans your drivers take out. Each one is also a stock location, so what is loaded is tracked while it moves."
        action={
          <AddButton onClick={() => setEditing('new')} disabled={!canManage}>
            Add vehicle
          </AddButton>
        }
      >
        {vehicles.data?.length ? (
          <ListShell>
            {vehicles.data.map((vehicle) => (
              <ListRow
                key={vehicle.id}
                title={vehicle.name}
                subtitle={
                  [
                    [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '),
                    vehicle.licensePlate,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'No details recorded'
                }
                badges={
                  <>
                    {!vehicle.active && <Badge variant="outline">Parked</Badge>}
                    {vehicle.insuranceExpiresAt && vehicle.insuranceExpiresAt < today && (
                      <Badge variant="destructive">Insurance expired</Badge>
                    )}
                  </>
                }
                onClick={canManage ? () => setEditing(vehicle) : undefined}
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>
            No vehicles yet. A driver can still deliver, but nothing tracks what is loaded.
          </EmptyState>
        )}
      </SectionCard>

      {editing && (
        <VehicleForm
          accountId={accountId}
          vehicle={editing === 'new' ? null : editing}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
          onCancel={() => setEditing(null)}
          onDelete={(vehicle) => remove.mutate(vehicle)}
        />
      )}
    </div>
  );
}

function VehicleForm({
  accountId,
  vehicle,
  onSaved,
  onCancel,
  onDelete,
}: {
  accountId: string;
  vehicle: VehicleRecord | null;
  onSaved: () => void;
  onCancel: () => void;
  onDelete: (vehicle: VehicleRecord) => void;
}) {
  const { values, errors, set, submit } = useForm(toValues(vehicle), schema);

  const save = useMutation({
    mutationFn: (parsed: Values) => {
      const input = {
        name: parsed.name,
        make: parsed.make || null,
        model: parsed.model || null,
        year: parsed.year ? Number(parsed.year) : null,
        color: parsed.color || null,
        licensePlate: parsed.licensePlate || null,
        vin: parsed.vin ? parsed.vin.toUpperCase() : null,
        insuranceExpiresAt: parsed.insuranceExpiresAt || null,
        registrationExpiresAt: parsed.registrationExpiresAt || null,
        capacityValueCapCents: Math.round(Number(parsed.valueCap) * 100),
        active: parsed.active,
      };

      return vehicle ? updateVehicle(accountId, vehicle, input) : createVehicle(accountId, input);
    },
    onSuccess: () => {
      toast.success(vehicle ? 'Vehicle saved.' : 'Vehicle added.');
      onSaved();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form onSubmit={submit((parsed) => save.mutate(parsed))} noValidate>
      <SectionCard
        title={vehicle ? vehicle.name : 'New vehicle'}
        description="The value cap is the most product this vehicle may carry at once; most states set it at $10,000."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="vehicle-name"
            label="Name"
            placeholder="Van 1"
            value={values.name}
            error={errors.name}
            onChange={(value) => set('name', value)}
          />
          <TextField
            id="vehicle-plate"
            label="Licence plate"
            value={values.licensePlate}
            error={errors.licensePlate}
            onChange={(value) => set('licensePlate', value)}
          />
          <TextField
            id="vehicle-make"
            label="Make"
            value={values.make}
            error={errors.make}
            onChange={(value) => set('make', value)}
          />
          <TextField
            id="vehicle-model"
            label="Model"
            value={values.model}
            error={errors.model}
            onChange={(value) => set('model', value)}
          />
          <TextField
            id="vehicle-year"
            label="Year"
            value={values.year}
            error={errors.year}
            onChange={(value) => set('year', value)}
          />
          <TextField
            id="vehicle-color"
            label="Colour"
            value={values.color}
            error={errors.color}
            onChange={(value) => set('color', value)}
          />
          <TextField
            id="vehicle-vin"
            label="VIN"
            description="11-17 characters. I, O and Q are never used."
            value={values.vin}
            error={errors.vin}
            onChange={(value) => set('vin', value)}
          />
          <TextField
            id="vehicle-cap"
            label="Value cap"
            description="Dollars of product allowed on board."
            value={values.valueCap}
            error={errors.valueCap}
            onChange={(value) => set('valueCap', value)}
          />
          <TextField
            id="vehicle-insurance"
            label="Insurance expires"
            type="date"
            value={values.insuranceExpiresAt}
            error={errors.insuranceExpiresAt}
            onChange={(value) => set('insuranceExpiresAt', value)}
          />
          <TextField
            id="vehicle-registration"
            label="Registration expires"
            type="date"
            value={values.registrationExpiresAt}
            error={errors.registrationExpiresAt}
            onChange={(value) => set('registrationExpiresAt', value)}
          />
        </div>

        <SwitchField
          id="vehicle-active"
          label="In service"
          description="Parked vehicles stop appearing on the dispatch board."
          checked={values.active}
          onChange={(checked) => set('active', checked)}
        />

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : vehicle ? 'Save vehicle' : 'Add vehicle'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>

          {vehicle && (
            <Button
              type="button"
              variant="ghost"
              className="text-destructive ml-auto"
              onClick={() => onDelete(vehicle)}
            >
              Delete
            </Button>
          )}
        </div>
      </SectionCard>
    </form>
  );
}
